var __addDisposableResource = (this && this.__addDisposableResource) || function (env, value, async) {
    if (value !== null && value !== void 0) {
        if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
        var dispose, inner;
        if (async) {
            if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
            dispose = value[Symbol.asyncDispose];
        }
        if (dispose === void 0) {
            if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
            dispose = value[Symbol.dispose];
            if (async) inner = dispose;
        }
        if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
        if (inner) dispose = function() { try { inner.call(this); } catch (e) { return Promise.reject(e); } };
        env.stack.push({ value: value, dispose: dispose, async: async });
    }
    else if (async) {
        env.stack.push({ async: true });
    }
    return value;
};
var __disposeResources = (this && this.__disposeResources) || (function (SuppressedError) {
    return function (env) {
        function fail(e) {
            env.error = env.hasError ? new SuppressedError(e, env.error, "An error was suppressed during disposal.") : e;
            env.hasError = true;
        }
        var r, s = 0;
        function next() {
            while (r = env.stack.pop()) {
                try {
                    if (!r.async && s === 1) return s = 0, env.stack.push(r), Promise.resolve().then(next);
                    if (r.dispose) {
                        var result = r.dispose.call(r.value);
                        if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) { fail(e); return next(); });
                    }
                    else s |= 1;
                }
                catch (e) {
                    fail(e);
                }
            }
            if (s === 1) return env.hasError ? Promise.reject(env.error) : Promise.resolve();
            if (env.hasError) throw env.error;
        }
        return next();
    };
})(typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
});
import { rethrow, setUVMessage, UV } from 'kerium';
import { decodeUTF8 } from 'utilium';
import * as constants from '../constants.js';
import { contextOf } from '../internal/contexts.js';
import { hasAccess, isDirectory, isSymbolicLink } from '../internal/inode.js';
import { basename, dirname, join, parse, resolve as resolvePath } from '../path.js';
import { normalizeMode, normalizePath } from '../utils.js';
import { checkAccess } from './config.js';
import { Dirent, ifToDt } from './dir.js';
import { Handle } from './file.js';
import * as flags from './flags.js';
import { resolveMount } from './shared.js';
import { cacheOf, lockPath } from './vcache.js';
import { emitChange } from './watchers.js';
/**
 * Resolves the mount and real path for a path.
 * Additionally, any stats fetched will be returned for de-duplication
 * @internal @hidden
 */
export async function resolve($, path, preserveSymlinks, extra) {
    path = resolvePath.call($, path);
    if (preserveSymlinks) {
        const resolved = resolveMount(path, $, extra);
        const stats = cacheOf(resolved.fs).get(resolved.path)?.inode ?? (await resolved.fs.stat(resolved.path).catch(() => undefined));
        return { ...resolved, fullPath: path, stats };
    }
    /* Try to resolve it directly. If this works,
    that means we don't need to perform any resolution for parent directories. */
    try {
        const resolved = resolveMount(path, $);
        // Stat it to make sure it exists. The vnode cache takes precedence since it may have unsynced changes
        const stats = cacheOf(resolved.fs).get(resolved.path)?.inode ?? (await resolved.fs.stat(resolved.path));
        if (!isSymbolicLink(stats)) {
            return { ...resolved, fullPath: path, stats };
        }
        const target = resolvePath.call($, dirname(path), await readlink.call($, path));
        return await resolve($, target, preserveSymlinks, extra);
    }
    catch {
        // Go the long way
    }
    const { base, dir } = parse(path);
    const realDir = dir == '/' ? '/' : (await resolve($, dir, false, extra)).fullPath;
    const maybePath = join(realDir, base);
    const resolved = resolveMount(maybePath, $);
    const stats = cacheOf(resolved.fs).get(resolved.path)?.inode
        ?? (await resolved.fs.stat(resolved.path).catch((e) => {
            if (e.code == 'ENOENT')
                return;
            throw setUVMessage(Object.assign(e, { syscall: 'stat', path: maybePath, ...extra }));
        }));
    if (!stats)
        return { ...resolved, fullPath: maybePath };
    if (!isSymbolicLink(stats)) {
        return { ...resolved, fullPath: maybePath, stats };
    }
    const target = resolvePath.call($, realDir, await readlink.call($, maybePath));
    return await resolve($, target, false, extra);
}
/**
 * Opens a file. This helper handles the complexity of file flags.
 * @internal
 */
export async function open($, path, opt) {
    path = normalizePath(path);
    const mode = normalizeMode(opt.mode, 0o644), flag = flags.parse(opt.flag);
    const $ex = { syscall: 'open', path };
    // eslint-disable-next-line prefer-const
    let { fs, path: resolved, stats } = await resolve($, path, opt.preserveSymlinks, $ex);
    if (!stats) {
        const env_1 = { stack: [], error: void 0, hasError: false };
        try {
            if (!(flag & constants.O_CREAT))
                throw UV('ENOENT', $ex);
            // Create the file
            const parentPath = dirname(resolved);
            const parentStats = await fs.stat(parentPath);
            if (checkAccess && !hasAccess($, parentStats, constants.W_OK))
                throw UV('EACCES', 'open', dirname(path));
            if (!isDirectory(parentStats))
                throw UV('ENOTDIR', 'open', dirname(path));
            if (!opt.allowDirectory && mode & constants.S_IFDIR)
                throw UV('EISDIR', 'open', path);
            // Serialize entry creation with other operations on the parent directory
            const _ = __addDisposableResource(env_1, await lockPath(fs, parentPath, 'rw', parentStats), false);
            // Another task may have created the file while we waited for the lock
            stats = cacheOf(fs).get(resolved)?.inode ?? (await fs.stat(resolved).catch(() => undefined));
            if (!stats) {
                const { euid: uid, egid: gid } = contextOf($).credentials;
                const inode = await fs.createFile(resolved, {
                    mode,
                    uid: parentStats.mode & constants.S_ISUID ? parentStats.uid : uid,
                    gid: parentStats.mode & constants.S_ISGID ? parentStats.gid : gid,
                });
                // A new entry in the parent directory, which is a 'rename' event
                emitChange($, 'rename', path);
                return new Handle($, path, resolved, flag, cacheOf(fs).ref(resolved, inode));
            }
        }
        catch (e_1) {
            env_1.error = e_1;
            env_1.hasError = true;
        }
        finally {
            __disposeResources(env_1);
        }
    }
    if (checkAccess && !hasAccess($, stats, flags.toMode(flag)))
        throw UV('EACCES', $ex);
    if (flag & constants.O_EXCL)
        throw UV('EEXIST', $ex);
    if (!opt.allowDirectory && mode & constants.S_IFDIR)
        throw UV('EISDIR', 'open', path);
    const handle = new Handle($, path, resolved, flag, cacheOf(fs).ref(resolved, stats));
    if (flag & constants.O_TRUNC)
        await handle.truncate(0);
    return handle;
}
export async function readlink(path) {
    path = normalizePath(path);
    const $ex = { syscall: 'readlink', path };
    const { fs, stats, path: resolved } = await resolve(this, path, true, $ex);
    if (!stats)
        throw UV('ENOENT', $ex);
    if (checkAccess && !hasAccess(this, stats, constants.R_OK))
        throw UV('EACCES', $ex);
    if (!isSymbolicLink(stats))
        throw UV('EINVAL', $ex);
    const size = stats.size;
    const data = new Uint8Array(size);
    await fs.read(resolved, data, 0, size);
    return decodeUTF8(data);
}
export async function mkdir(path, options = {}) {
    const original = normalizePath(path);
    const { euid: uid, egid: gid } = contextOf(this).credentials;
    const { mode = 0o777, recursive } = options;
    const $ex = { syscall: 'mkdir', path: original };
    const { fullPath: realParent } = await resolve(this, dirname(original), false, $ex);
    const { fs, path: target } = resolveMount(join(realParent, basename(original)), this, $ex);
    const followed = realParent != dirname(original);
    let firstCreated;
    const __create = async (path, resolved) => {
        const env_2 = { stack: [], error: void 0, hasError: false };
        try {
            const parentPath = dirname(resolved);
            const parent = recursive && parentPath != '/' ? await __create(dirname(path), parentPath) : await fs.stat(parentPath);
            const _ = __addDisposableResource(env_2, await lockPath(fs, parentPath, 'rw', parent), false);
            if (recursive) {
                const existing = cacheOf(fs).get(resolved)?.inode ?? (await fs.stat(resolved).catch(() => null));
                if (existing) {
                    const stats = isSymbolicLink(existing) ? (await resolve(this, path, false, $ex)).stats : existing;
                    if (!stats)
                        throw UV('ENOENT', $ex);
                    if (!isDirectory(stats))
                        throw UV(resolved == target ? 'EEXIST' : 'ENOTDIR', $ex);
                    return existing;
                }
                // Node reports the link itself with ENOTDIR here, while `mkdirSync` reports the requested path with ENOENT
                if (followed && (await fs.exists(join(parentPath, basename(path)))))
                    throw UV('ENOTDIR', 'mkdir', path);
            }
            if (checkAccess && !hasAccess(this, parent, constants.W_OK))
                throw UV('EACCES', 'mkdir', path);
            const inode = await fs
                .mkdir(resolved, {
                mode,
                uid: parent.mode & constants.S_ISUID ? parent.uid : uid,
                gid: parent.mode & constants.S_ISGID ? parent.gid : gid,
            })
                .catch(rethrow({ syscall: 'mkdir', path }));
            if (recursive)
                firstCreated ??= path;
            emitChange(this, 'rename', path);
            return inode;
        }
        catch (e_2) {
            env_2.error = e_2;
            env_2.hasError = true;
        }
        finally {
            __disposeResources(env_2);
        }
    };
    await __create(original, target);
    return firstCreated;
}
export async function readdir(path, options = {}) {
    path = normalizePath(path);
    // Node reports `readdir` failures as `scandir`
    const $ex = { syscall: 'scandir', path };
    const { fs, path: resolved, stats } = await resolve(this, path, false, $ex);
    if (!stats)
        throw UV('ENOENT', $ex);
    if (checkAccess && !hasAccess(this, stats, constants.R_OK))
        throw UV('EACCES', $ex);
    if (!isDirectory(stats))
        throw UV('ENOTDIR', $ex);
    let entries;
    {
        const env_3 = { stack: [], error: void 0, hasError: false };
        try {
            const _ = __addDisposableResource(env_3, await lockPath(fs, resolved, 'ro', stats), false);
            entries = await fs.readdir(resolved);
        }
        catch (e_3) {
            env_3.error = e_3;
            env_3.hasError = true;
        }
        finally {
            __disposeResources(env_3);
        }
    }
    const values = [];
    const addEntry = async (entry) => {
        const entryStats = await fs.stat(join(resolved, entry)).catch((e) => {
            if (e.code == 'ENOENT')
                return;
            throw e;
        });
        if (!entryStats)
            return;
        const ent = new Dirent();
        ent.ino = entryStats.ino;
        ent.type = ifToDt(entryStats.mode);
        ent.path = entry;
        ent.name = basename(entry);
        values.push(ent);
        if (!options.recursive || !isDirectory(entryStats))
            return;
        const children = await fs.readdir(join(resolved, entry));
        for (const child of children)
            await addEntry(join(entry, child));
    };
    await Promise.all(entries.map(addEntry));
    return values;
}
export async function rename(oldPath, newPath) {
    const env_4 = { stack: [], error: void 0, hasError: false };
    try {
        oldPath = normalizePath(oldPath);
        newPath = normalizePath(newPath);
        const $ex = { syscall: 'rename', path: oldPath, dest: newPath };
        const src = await resolve(this, oldPath, true, $ex);
        const dst = resolveMount(newPath, this, $ex);
        if (src.fs.uuid !== dst.fs.uuid)
            throw UV('EXDEV', $ex);
        // A directory can not be moved inside itself
        if (dst.path.startsWith(src.path + '/'))
            throw UV('EINVAL', $ex);
        if (!src.stats)
            throw UV('ENOENT', $ex);
        const fs = src.fs;
        const oldParent = await fs.stat(dirname(src.path));
        const newParent = await fs.stat(dirname(dst.path));
        const newStats = await fs.stat(dst.path).catch((e) => {
            if (e.code == 'ENOENT')
                return null;
            throw e;
        });
        if (checkAccess && (!hasAccess(this, oldParent, constants.R_OK) || !hasAccess(this, newParent, constants.W_OK)))
            throw UV('EACCES', $ex);
        if (newStats && !isDirectory(src.stats) && isDirectory(newStats))
            throw UV('EISDIR', $ex);
        if (newStats && isDirectory(src.stats) && !isDirectory(newStats))
            throw UV('ENOTDIR', $ex);
        const srcDir = dirname(src.path);
        const dstDir = dirname(dst.path);
        // Lock both parent directories, ordered by inode number to avoid ABBA deadlocks (like Linux's `lock_two_nondirectories`)
        const parents = [
            [srcDir, oldParent],
            [dstDir, newParent],
        ];
        if (oldParent.ino > newParent.ino)
            parents.reverse();
        const _first = __addDisposableResource(env_4, await lockPath(fs, parents[0][0], 'rw', parents[0][1]), false);
        const _second = __addDisposableResource(env_4, oldParent.ino == newParent.ino ? null : await lockPath(fs, parents[1][0], 'rw', parents[1][1]), false);
        await src.fs.rename(src.path, dst.path);
        cacheOf(fs).rename(src.path, dst.path);
        // Both names change which entries exist, so both are 'rename' events
        emitChange(this, 'rename', oldPath);
        emitChange(this, 'rename', newPath);
    }
    catch (e_4) {
        env_4.error = e_4;
        env_4.hasError = true;
    }
    finally {
        __disposeResources(env_4);
    }
}
export async function link(target, link) {
    const env_5 = { stack: [], error: void 0, hasError: false };
    try {
        target = normalizePath(target);
        link = normalizePath(link);
        const $ex = { syscall: 'link', path: target, dest: link };
        const { fs, path: resolved } = resolveMount(target, this, $ex);
        const dst = resolveMount(link, this, $ex);
        if (fs.uuid != dst.fs.uuid)
            throw UV('EXDEV', $ex);
        const stats = await fs.stat(resolved);
        if (checkAccess) {
            if (!hasAccess(this, stats, constants.R_OK))
                throw UV('EACCES', $ex);
            const dirStats = await fs.stat(dirname(resolved));
            if (!hasAccess(this, dirStats, constants.R_OK))
                throw UV('EACCES', $ex);
            const destStats = await fs.stat(dirname(dst.path));
            if (!hasAccess(this, destStats, constants.W_OK))
                throw UV('EACCES', $ex);
        }
        const _ = __addDisposableResource(env_5, await lockPath(fs, dirname(dst.path), 'rw'), false);
        await fs.link(resolved, dst.path);
        cacheOf(fs).link(resolved, dst.path);
    }
    catch (e_5) {
        env_5.error = e_5;
        env_5.hasError = true;
    }
    finally {
        __disposeResources(env_5);
    }
}
export async function stat(path, lstat) {
    path = normalizePath.call(this, path);
    const extra = { syscall: lstat ? 'lstat' : 'stat', path };
    let stats;
    if (!lstat)
        stats = (await resolve(this, path, false, extra)).stats;
    else {
        const { base, dir } = parse(path);
        const { fs, path: parent } = await resolve(this, dir, false, extra);
        const target = base ? join(parent, base) : parent;
        stats = cacheOf(fs).get(target)?.inode ?? (await fs.stat(target).catch(rethrow(extra)));
    }
    if (!stats)
        throw UV('ENOENT', extra);
    if (checkAccess && !hasAccess(this, stats, constants.R_OK))
        throw UV('EACCES', extra);
    return stats;
}
